import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, IconButton, Button, Chip,
  CircularProgress, Alert, Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tab, Tabs, useTheme, alpha, LinearProgress,
  Badge, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  LockClock, SportsSoccer, EmojiEvents, Leaderboard, FormatListBulleted,
  Add, Remove, Sync, CheckCircle, Cancel
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import UserAvatar from '../components/UserAvatar';
import { getTeamFlagUrl } from '../utils/wcFlags';
import { ROLE } from '../utils/roles';
import wcService, {
  WCMatch, WCPrediction, WCRankingEntry, SavePredictionPayload,
  WCPredictionsOverview, WCUserPredictionEntry, WCPredictorUser
} from '../services/wcService';
import { useTabSearchParam } from '../hooks/useTabSearchParam';

// ─── Design Tokens (Nautilus Console) ─────────────────────────────────────────
const N = {
  deepOcean: '#0f2134',
  sand:      '#ccba9b',
  abyssal:   '#0a1622',
  pitch:     '#1a6b3c',   // football-pitch green — used sparingly for "live"
  gold:      '#d4a017',   // trophy accent for rankings
};

const STAGE_LABELS: Record<string, string> = {
  LAST_16:       'Round of 16',
  QUARTER_FINALS:'Quarter-Finals',
  SEMI_FINALS:   'Semi-Finals',
  THIRD_PLACE:   'Third Place',
  FINAL:         'Final',
};

const BRACKET_ROUNDS = ['LAST_16', 'QUARTER_FINALS', 'SEMI_FINALS', 'FINAL'] as const;

/** Vertical spacing unit for bracket positioning */
const BRACKET_SLOT = 88;
const BRACKET_CARD_H = 118;

function bracketMatchTop(roundIndex: number, matchIndex: number): number {
  const span = Math.pow(2, roundIndex + 1) * BRACKET_SLOT;
  const offset = (Math.pow(2, roundIndex) - 1) * BRACKET_SLOT;
  return matchIndex * span + offset;
}

function bracketTotalHeight(r16Count: number): number {
  return Math.max(r16Count, 1) * 2 * BRACKET_SLOT;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isLocked(match: WCMatch): boolean {
  return new Date(match.kickoff_utc) <= new Date();
}

function formatKickoff(utc: string): string {
  return new Date(utc).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function predKey(matchId: number, preds: WCPrediction[]): WCPrediction | undefined {
  return preds.find(p => p.match_id === matchId);
}

const TeamName: React.FC<{ name: string; sx?: object }> = ({ name, sx }) => {
  const flagUrl = getTeamFlagUrl(name);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ...sx }}>
      {flagUrl && (
        <Box
          component="img"
          src={flagUrl}
          alt=""
          loading="lazy"
          sx={{
            width: 18,
            height: 13,
            objectFit: 'cover',
            borderRadius: '2px',
            flexShrink: 0,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
          }}
        />
      )}
      <Box component="span">{name}</Box>
    </Box>
  );
};

function hasPenaltyShootout(penaltyHomeScore: number | null, penaltyAwayScore: number | null): boolean {
  return penaltyHomeScore != null && penaltyAwayScore != null;
}

// ─── Score Stepper ────────────────────────────────────────────────────────────
interface StepperProps {
  value: number;
  onChange: (v: number) => void;
  disabled: boolean;
  isDark: boolean;
}
const ScoreStepper: React.FC<StepperProps> = ({ value, onChange, disabled, isDark }) => {
  const border = isDark ? `1px solid rgba(204,186,155,0.2)` : `1px solid rgba(15,33,52,0.12)`;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <IconButton
        size="small" disabled={disabled || value <= 0}
        onClick={() => onChange(Math.max(0, value - 1))}
        sx={{ width: 24, height: 24, border, borderRadius: '4px', opacity: disabled ? 0.4 : 1 }}
      >
        <Remove sx={{ fontSize: 14 }} />
      </IconButton>
      <Box sx={{
        minWidth: 28, textAlign: 'center',
        fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem',
        color: isDark ? '#fff' : N.deepOcean
      }}>
        {value}
      </Box>
      <IconButton
        size="small" disabled={disabled || value >= 20}
        onClick={() => onChange(Math.min(20, value + 1))}
        sx={{ width: 24, height: 24, border, borderRadius: '4px', opacity: disabled ? 0.4 : 1 }}
      >
        <Add sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};

// ─── Bracket Match Card (compact, vertical) ───────────────────────────────────
interface BracketMatchCardProps {
  match: WCMatch;
  prediction: WCPrediction | undefined;
  isDark: boolean;
  onPredictionChange: (matchId: number, field: 'home' | 'away' | 'adv', value: number | string) => void;
  pendingPreds: Record<number, { home: number; away: number; adv: string }>;
}

const BracketMatchCard: React.FC<BracketMatchCardProps> = ({
  match, prediction, isDark, onPredictionChange, pendingPreds
}) => {
  const locked = isLocked(match);
  const panelBorder = isDark ? 'rgba(204,186,155,0.15)' : '#e0e0e0';
  const surfaceBg   = isDark ? N.deepOcean : '#ffffff';
  const headerBg    = isDark ? alpha(N.sand, 0.07) : alpha(N.deepOcean, 0.03);

  const pending = pendingPreds[match.id];
  const homeVal = pending?.home ?? prediction?.home_score_pred ?? 0;
  const awayVal = pending?.away ?? prediction?.away_score_pred ?? 0;
  const advVal  = pending?.adv  ?? prediction?.advancing_team ?? '';

  const homeTeam = match.home_team || 'TBD';
  const awayTeam = match.away_team || 'TBD';
  const isTBD    = !match.home_team || !match.away_team;
  const isLive   = match.status === 'IN_PLAY' || match.status === 'PAUSED';
  const isFinished = match.status === 'FINISHED';

  const teamRow = (team: string, align: 'left' | 'right') => {
    const selected = advVal === team;
    const wasWinner = isFinished && match.winner === team;
    return (
      <Box
        onClick={() => !locked && !isFinished && !isTBD && onPredictionChange(match.id, 'adv', team)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
          px: 0.75, py: 0.4,
          borderRadius: '4px',
          cursor: locked || isFinished || isTBD ? 'default' : 'pointer',
          bgcolor: selected ? alpha(N.sand, isDark ? 0.15 : 0.1) : 'transparent',
          border: selected ? `1px solid ${N.sand}` : '1px solid transparent',
          opacity: isTBD ? 0.55 : 1,
          transition: 'all 0.15s',
          '&:hover': !locked && !isFinished && !isTBD ? {
            bgcolor: alpha(N.sand, 0.08),
            border: `1px solid ${alpha(N.sand, 0.4)}`,
          } : {},
        }}
      >
        {wasWinner && <CheckCircle sx={{ fontSize: 11, color: N.pitch, flexShrink: 0 }} />}
        <Typography sx={{
          fontSize: '0.68rem',
          fontWeight: selected ? 700 : 400,
          color: selected ? N.sand : 'text.primary',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          textAlign: align,
        }}>
          <TeamName name={team} />
        </Typography>
      </Box>
    );
  };

  return (
    <Card sx={{
      width: 200,
      border: `1px solid ${panelBorder}`,
      borderRadius: 1,
      bgcolor: surfaceBg,
      boxShadow: 'none',
      overflow: 'hidden',
    }}>
      <Box sx={{
        px: 1, py: 0.4, bgcolor: headerBg,
        borderBottom: `1px solid ${panelBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5,
      }}>
        <Typography sx={{
          fontSize: '0.52rem', fontWeight: 600, color: 'text.disabled',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {formatKickoff(match.kickoff_utc)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', flexShrink: 0 }}>
          {isLive && (
            <Chip label="LIVE" size="small" sx={{
              fontSize: '0.5rem', height: 14, fontWeight: 700,
              bgcolor: N.pitch, color: '#fff',
            }} />
          )}
          {isFinished && (
            <Chip label="FT" size="small" sx={{
              fontSize: '0.5rem', height: 14, fontWeight: 700,
              bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
              color: 'text.secondary',
            }} />
          )}
          {locked && !isFinished && !isLive && (
            <LockClock sx={{ fontSize: 11, color: 'text.disabled' }} />
          )}
        </Box>
      </Box>

      <CardContent sx={{ p: 0.75, '&:last-child': { pb: 0.75 } }}>
        {teamRow(homeTeam, 'left')}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.25 }}>
          {isFinished ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{
                fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem',
                color: isDark ? '#fff' : N.deepOcean,
              }}>
                {match.home_score ?? '—'} – {match.away_score ?? '—'}
              </Typography>
              {hasPenaltyShootout(match.penalty_home_score, match.penalty_away_score) && (
                <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', lineHeight: 1.1 }}>
                  Pens: {match.penalty_home_score} - {match.penalty_away_score}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <ScoreStepper value={homeVal} onChange={v => onPredictionChange(match.id, 'home', v)} disabled={locked || isTBD} isDark={isDark} />
              <Typography sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>–</Typography>
              <ScoreStepper value={awayVal} onChange={v => onPredictionChange(match.id, 'away', v)} disabled={locked || isTBD} isDark={isDark} />
            </Box>
          )}
        </Box>

        {teamRow(awayTeam, 'right')}
      </CardContent>
    </Card>
  );
};

// ─── Bracket connector lines ──────────────────────────────────────────────────
interface BracketConnectorsProps {
  roundIndex: number;
  matchCount: number;
  totalHeight: number;
  isDark: boolean;
}

const BracketConnectors: React.FC<BracketConnectorsProps> = ({
  roundIndex, matchCount, totalHeight, isDark
}) => {
  const lineColor = isDark ? 'rgba(204,186,155,0.3)' : 'rgba(15,33,52,0.2)';
  const halfCard = BRACKET_CARD_H / 2;
  const labelOffset = 22; // stage label height above matches

  return (
    <Box sx={{ width: 28, flexShrink: 0, pt: `${labelOffset}px` }}>
      <svg width={28} height={totalHeight} style={{ display: 'block', overflow: 'visible' }}>
        {Array.from({ length: matchCount }).map((_, i) => {
          const y1 = bracketMatchTop(roundIndex - 1, i * 2) + halfCard;
          const y2 = bracketMatchTop(roundIndex - 1, i * 2 + 1) + halfCard;
          const yChild = bracketMatchTop(roundIndex, i) + halfCard;
          const yMid = (y1 + y2) / 2;
          return (
            <path
              key={i}
              d={`M 0 ${y1} H 14 V ${y2} M 14 ${yMid} H 28 V ${yChild}`}
              fill="none"
              stroke={lineColor}
              strokeWidth={1}
            />
          );
        })}
      </svg>
    </Box>
  );
};

// ─── Bracket View ─────────────────────────────────────────────────────────────
interface BracketViewProps {
  matches: WCMatch[];
  predictions: WCPrediction[];
  isDark: boolean;
  onPredictionChange: (matchId: number, field: 'home' | 'away' | 'adv', value: number | string) => void;
  pendingPreds: Record<number, { home: number; away: number; adv: string }>;
}

const BracketView: React.FC<BracketViewProps> = ({ matches, predictions, isDark, onPredictionChange, pendingPreds }) => {
  const byStage = useMemo(() => {
    const map: Record<string, WCMatch[]> = {};
    for (const m of matches) {
      if (!map[m.stage]) map[m.stage] = [];
      map[m.stage].push(m);
    }
    for (const stage in map) {
      map[stage].sort((a, b) => a.bracket_pos - b.bracket_pos);
    }
    return map;
  }, [matches]);

  const r16Count = byStage['LAST_16']?.length || 8;
  const totalHeight = bracketTotalHeight(r16Count);
  const panelBorder = isDark ? 'rgba(204,186,155,0.15)' : '#e0e0e0';

  const hasBracket = BRACKET_ROUNDS.some(s => byStage[s]?.length);

  if (!hasBracket && !byStage['THIRD_PLACE']?.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <SportsSoccer sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography color="text.secondary">No matches scheduled yet. Sync to load fixtures.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 980, position: 'relative' }}>
          {BRACKET_ROUNDS.map((stage, roundIdx) => {
            const stageMatches = byStage[stage] || [];
            if (!stageMatches.length) return null;

            return (
              <React.Fragment key={stage}>
                {roundIdx > 0 && (
                  <BracketConnectors
                    roundIndex={roundIdx}
                    matchCount={stageMatches.length}
                    totalHeight={totalHeight}
                    isDark={isDark}
                  />
                )}
                <Box sx={{ width: 200, flexShrink: 0, position: 'relative', height: totalHeight + 22 }}>
                  <Typography sx={{
                    fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: N.sand, mb: 1, textAlign: 'center',
                  }}>
                    {STAGE_LABELS[stage]}
                  </Typography>
                  {stageMatches.map((match, idx) => (
                    <Box
                      key={match.id}
                      sx={{
                        position: 'absolute',
                        top: 22 + bracketMatchTop(roundIdx, idx),
                        left: 0,
                        width: 200,
                      }}
                    >
                      <BracketMatchCard
                        match={match}
                        prediction={predKey(match.id, predictions)}
                        isDark={isDark}
                        onPredictionChange={onPredictionChange}
                        pendingPreds={pendingPreds}
                      />
                    </Box>
                  ))}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {/* Third-place match — shown below the bracket */}
      {byStage['THIRD_PLACE']?.length > 0 && (
        <Box sx={{
          mt: 3, pt: 2, borderTop: `1px dashed ${panelBorder}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        }}>
          <Typography sx={{
            fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: N.sand,
          }}>
            {STAGE_LABELS['THIRD_PLACE']}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {byStage['THIRD_PLACE'].map(match => (
              <BracketMatchCard
                key={match.id}
                match={match}
                prediction={predKey(match.id, predictions)}
                isDark={isDark}
                onPredictionChange={onPredictionChange}
                pendingPreds={pendingPreds}
              />
            ))}
          </Box>
        </Box>
      )}

      <Typography sx={{
        mt: 2, fontSize: '0.6rem', color: 'text.disabled', textAlign: 'center',
      }}>
        Click a team to pick the winner · Scroll horizontally on smaller screens
      </Typography>
    </Box>
  );
};

// ─── Leaderboard View ─────────────────────────────────────────────────────────
interface LeaderboardViewProps {
  entries: WCRankingEntry[];
  currentUserId: number;
  isDark: boolean;
}

const LeaderboardView: React.FC<LeaderboardViewProps> = ({ entries, currentUserId, isDark }) => {
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const headerBg    = isDark ? alpha(N.sand, 0.07) : alpha(N.deepOcean, 0.03);

  if (!entries.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Leaderboard sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography color="text.secondary">No rankings yet — predictions are being scored.</Typography>
      </Box>
    );
  }

  const maxPts = entries[0]?.points || 1;

  return (
    <TableContainer sx={{
      border: `1px solid ${panelBorder}`, borderRadius: 1, bgcolor: 'background.paper'
    }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: headerBg }}>
            {['Rank', 'Player', 'Points', 'Exact', 'Error', 'Champion'].map(h => (
              <TableCell key={h} sx={{
                fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: 'text.secondary',
                borderBottom: `1px solid ${panelBorder}`, py: 1, whiteSpace: 'nowrap'
              }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry, i) => {
            const isMe = entry.user_id === currentUserId;
            const isTop3 = entry.rank <= 3;
            const rankColor = entry.rank === 1 ? N.gold : entry.rank === 2 ? '#aaa' : entry.rank === 3 ? '#cd7f32' : 'text.secondary';

            return (
              <TableRow
                key={entry.user_id}
                sx={{
                  bgcolor: isMe
                    ? alpha(N.sand, isDark ? 0.08 : 0.05)
                    : i % 2 === 0
                    ? 'transparent'
                    : isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.01)',
                  '&:hover': { bgcolor: alpha(N.sand, 0.05) },
                  transition: 'background-color 0.15s',
                  borderLeft: isMe ? `3px solid ${N.sand}` : '3px solid transparent',
                }}
              >
                {/* Rank */}
                <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isTop3 ? (
                      <EmojiEvents sx={{ fontSize: 14, color: rankColor }} />
                    ) : (
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.disabled' }}>
                        #{entry.rank}
                      </Typography>
                    )}
                    {isTop3 && (
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 700, color: rankColor }}>
                        #{entry.rank}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Player name */}
                <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <UserAvatar
                      name={entry.user_name}
                      src={entry.profile_picture ?? undefined}
                      sx={{ width: 28, height: 28, fontSize: '0.7rem', flexShrink: 0 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{
                          fontSize: '0.8rem',
                          fontWeight: isMe ? 700 : 400,
                          color: isMe ? N.sand : 'text.primary',
                        }}>
                          {entry.user_name}
                        </Typography>
                        {isMe && (
                          <Chip label="you" size="small" sx={{
                            height: 14, fontSize: '0.55rem', fontWeight: 700,
                            bgcolor: alpha(N.sand, 0.15), color: N.sand,
                            letterSpacing: '0.05em'
                          }} />
                        )}
                      </Box>
                      {/* Points progress bar */}
                      <LinearProgress
                        variant="determinate"
                        value={(entry.points / maxPts) * 100}
                        sx={{
                          mt: 0.5, height: 2, borderRadius: 1,
                          bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
                          '& .MuiLinearProgress-bar': { bgcolor: isMe ? N.sand : N.deepOcean }
                        }}
                      />
                    </Box>
                  </Box>
                </TableCell>

                {/* Points */}
                <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                  <Typography sx={{
                    fontSize: '0.9rem', fontFamily: 'monospace', fontWeight: 700,
                    color: isTop3 ? rankColor : 'text.primary'
                  }}>
                    {entry.points}
                  </Typography>
                </TableCell>

                {/* Exact scores */}
                <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                  <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                    {entry.exact_scores}
                  </Typography>
                </TableCell>

                {/* Score error */}
                <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                  <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                    {entry.score_error}
                  </Typography>
                </TableCell>

                {/* Champion correct */}
                <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                  {entry.champion_correct ? (
                    <CheckCircle sx={{ fontSize: 16, color: N.pitch }} />
                  ) : (
                    <Cancel sx={{ fontSize: 16, color: 'text.disabled' }} />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ px: 1.5, py: 1, borderTop: `1px solid ${panelBorder}` }}>
        <Typography sx={{ fontSize: '0.58rem', color: 'text.disabled' }}>
          Ties broken by exact scores → correct winners → lowest score error.
        </Typography>
      </Box>
    </TableContainer>
  );
};

// ─── User Predictions View ────────────────────────────────────────────────────
interface UserPredictionsViewProps {
  currentUserId: number;
  isDark: boolean;
}

const UserPredictionsView: React.FC<UserPredictionsViewProps> = ({ currentUserId, isDark }) => {
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const headerBg    = isDark ? alpha(N.sand, 0.07) : alpha(N.deepOcean, 0.03);

  const [selectedUserId, setSelectedUserId] = useState<number>(currentUserId);
  const [overview, setOverview] = useState<WCPredictionsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedUserId(currentUserId);
  }, [currentUserId]);

  const loadOverview = useCallback(async (userId: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await wcService.getPredictionsOverview(userId);
      setOverview(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load predictions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUserId > 0) {
      loadOverview(selectedUserId);
    }
  }, [selectedUserId, currentUserId, loadOverview]);

  const grouped = useMemo(() => {
    if (!overview?.predictions.length) return [];
    const groups: { stage: string; items: WCUserPredictionEntry[] }[] = [];
    let currentStage = '';
    for (const p of overview.predictions) {
      if (p.match_stage !== currentStage) {
        currentStage = p.match_stage;
        groups.push({ stage: currentStage, items: [] });
      }
      groups[groups.length - 1].items.push(p);
    }
    return groups;
  }, [overview?.predictions]);

  const users = useMemo(() => {
    const seen = new Map<number, WCPredictorUser>();
    for (const u of overview?.users ?? []) {
      seen.set(u.user_id, u);
    }
    return Array.from(seen.values());
  }, [overview?.users]);

  if (loading && !overview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={36} sx={{ color: N.sand }} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap',
      }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="wc-predictor-select">Player</InputLabel>
          <Select
            labelId="wc-predictor-select"
            label="Player"
            value={selectedUserId}
            onChange={e => setSelectedUserId(Number(e.target.value))}
            sx={{ fontSize: '0.85rem' }}
          >
            {users.map(u => (
              <MenuItem key={u.user_id} value={u.user_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <UserAvatar
                    name={u.user_name}
                    src={u.profile_picture ?? undefined}
                    sx={{ width: 24, height: 24, fontSize: '0.65rem' }}
                  />
                  <Typography sx={{ fontSize: '0.85rem', flex: 1 }}>
                    {u.user_name}
                    {u.user_id === currentUserId ? ' (you)' : ''}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                    {u.prediction_count}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {overview?.is_self ? (
          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
            Showing all your predictions, including upcoming matches.
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
            Showing predictions for completed matches only.
          </Typography>
        )}
      </Box>

      {!overview?.predictions.length ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <FormatListBulleted sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">
            {overview?.is_self
              ? 'You have not submitted any predictions yet.'
              : 'No finished-match predictions for this player.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{
          border: `1px solid ${panelBorder}`, borderRadius: 1, bgcolor: 'background.paper',
          opacity: loading ? 0.6 : 1,
        }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: headerBg }}>
                {['Match', 'Prediction', 'Pick', 'Result', 'Pts'].map(h => (
                  <TableCell key={h} sx={{
                    fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'text.secondary',
                    borderBottom: `1px solid ${panelBorder}`, py: 1, whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map(group => (
                <React.Fragment key={group.stage}>
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{
                        py: 0.75, bgcolor: alpha(N.sand, isDark ? 0.06 : 0.04),
                        borderBottom: `1px solid ${panelBorder}`,
                      }}
                    >
                      <Typography sx={{
                        fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
                        textTransform: 'uppercase', color: N.sand,
                      }}>
                        {STAGE_LABELS[group.stage] || group.stage}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {group.items.map(p => {
                    const finished = p.status === 'FINISHED';
                    const home = p.home_team || 'TBD';
                    const away = p.away_team || 'TBD';
                    return (
                      <TableRow key={p.id} sx={{ '&:hover': { bgcolor: alpha(N.sand, 0.04) } }}>
                        <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            <TeamName name={home} /> vs <TeamName name={away} />
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: 'text.disabled', mt: 0.25 }}>
                            {p.kickoff_utc ? formatKickoff(p.kickoff_utc) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                          <Typography sx={{
                            fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 600,
                          }}>
                            {p.home_score_pred} – {p.away_score_pred}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                          <Typography sx={{ fontSize: '0.75rem' }}>
                            <TeamName name={p.advancing_team} />
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                          {finished ? (
                            <Box>
                              <Typography sx={{
                                fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 600,
                              }}>
                                {p.home_score ?? '—'} – {p.away_score ?? '—'}
                              </Typography>
                              {hasPenaltyShootout(p.penalty_home_score, p.penalty_away_score) && (
                                <Typography sx={{ fontSize: '0.56rem', color: 'text.disabled', mt: 0.15 }}>
                                  Pens: {p.penalty_home_score} - {p.penalty_away_score}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                {p.correct_winner ? (
                                  <CheckCircle sx={{ fontSize: 13, color: N.pitch }} />
                                ) : (
                                  <Cancel sx={{ fontSize: 13, color: 'text.disabled' }} />
                                )}
                                <Typography sx={{ fontSize: '0.58rem', color: 'text.disabled' }}>
                                  {p.exact_score ? 'Exact score' : p.correct_winner ? 'Winner' : 'Wrong'}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Chip label="Upcoming" size="small" sx={{
                              height: 18, fontSize: '0.55rem', fontWeight: 600,
                              color: 'text.secondary',
                            }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: `1px solid ${panelBorder}` }}>
                          {finished && p.points_earned != null ? (
                            <Typography sx={{
                              fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 700,
                              color: p.points_earned > 0 ? N.sand : 'text.disabled',
                            }}>
                              {p.points_earned}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const WC2026Page: React.FC = () => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();

  const [tab, setTab] = useTabSearchParam(
    ['bracket', 'predictions', 'leaderboard'] as const,
    'bracket',
  );
  const [matches, setMatches]   = useState<WCMatch[]>([]);
  const [predictions, setPreds] = useState<WCPrediction[]>([]);
  const [leaderboard, setLB]    = useState<WCRankingEntry[]>([]);
  const [loading, setLoading]   = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [saveMsg, setSaveMsg]   = useState<string | null>(null);
  const [pendingPreds, setPending] = useState<Record<number, { home: number; away: number; adv: string }>>({});

  const isAdminOrPM = user?.roles?.includes(ROLE.ADMIN) || user?.roles?.includes(ROLE.PM);

  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';

  // ── Load data ────────────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, p, lb] = await Promise.all([
        wcService.getMatches(),
        wcService.getPredictions(),
        wcService.getLeaderboard(),
      ]);
      setMatches(m);
      setPreds(p);
      setLB(lb);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load WC2026 data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Prediction change ─────────────────────────────────────────────────────
  const handlePredictionChange = useCallback(
    (matchId: number, field: 'home' | 'away' | 'adv', value: number | string) => {
      const match = matches.find(m => m.id === matchId);
      if (!match) return;

      // Bootstrap pending from existing prediction
      const existing = predictions.find(p => p.match_id === matchId);
      setPending(prev => {
        const cur = prev[matchId] ?? {
          home: existing?.home_score_pred ?? 0,
          away: existing?.away_score_pred ?? 0,
          adv:  existing?.advancing_team ?? '',
        };

        let updated = { ...cur };
        if (field === 'home') updated.home = value as number;
        if (field === 'away') updated.away = value as number;
        if (field === 'adv')  updated.adv  = value as string;

        // Auto-set advancing team when score becomes unambiguous
        if (field === 'home' || field === 'away') {
          const newHome = field === 'home' ? (value as number) : cur.home;
          const newAway = field === 'away' ? (value as number) : cur.away;
          if (newHome !== newAway) {
            updated.adv = newHome > newAway
              ? (match.home_team || '')
              : (match.away_team || '');
          }
        }

        return { ...prev, [matchId]: updated };
      });
    },
    [matches, predictions]
  );

  // ── Save predictions ──────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const payload: SavePredictionPayload[] = Object.entries(pendingPreds)
        .filter(([, v]) => v.adv !== '')
        .map(([matchId, v]) => ({
          match_id: parseInt(matchId),
          home_score_pred: v.home,
          away_score_pred: v.away,
          advancing_team: v.adv,
        }));

      if (!payload.length) {
        setSaveMsg('No predictions to save.');
        setSaving(false);
        return;
      }
      await wcService.savePredictions(payload);
      setSaveMsg(`Saved ${payload.length} prediction${payload.length > 1 ? 's' : ''}!`);
      setPending({});
      // Refresh predictions & leaderboard
      const [p, lb] = await Promise.all([wcService.getPredictions(), wcService.getLeaderboard()]);
      setPreds(p);
      setLB(lb);
    } catch (e: any) {
      setSaveMsg(e?.response?.data?.error || 'Failed to save predictions');
    } finally {
      setSaving(false);
    }
  };

  // ── Manual sync ─────────────────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await wcService.syncMatches();
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // ── Pending count ────────────────────────────────────────────────────────
  const pendingCount = Object.values(pendingPreds).filter(v => v.adv !== '').length;

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Page header ── */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 2, mb: 3,
        pb: 2, borderBottom: `1px solid ${panelBorder}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1,
            bgcolor: alpha(N.sand, 0.12),
            border: `1px solid ${alpha(N.sand, 0.3)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <EmojiEvents sx={{ fontSize: 26, color: N.sand }} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1" sx={{
              fontWeight: 700, fontSize: '1.3rem',
              letterSpacing: '-0.01em', color: isDark ? '#fff' : N.deepOcean
            }}>
              WC 2026
            </Typography>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'text.secondary', mt: 0.2
            }}>
              Knockout Stage Prediction Game
            </Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {tab === 'bracket' && pendingCount > 0 && (
            <Button
              variant="contained"
              size="small"
              disabled={saving}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={14} /> : <CheckCircle />}
              sx={{
                bgcolor: N.sand, color: N.deepOcean, fontWeight: 700,
                fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                boxShadow: 'none',
                '&:hover': { bgcolor: alpha(N.sand, 0.85), boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: alpha(N.sand, 0.4), color: N.deepOcean }
              }}
            >
              Save {pendingCount} Prediction{pendingCount > 1 ? 's' : ''}
            </Button>
          )}
          {isAdminOrPM && (
            <Tooltip title="Sync match data & results from football-data.org">
              <Button
                size="small"
                variant="outlined"
                onClick={handleSync}
                disabled={syncing}
                startIcon={syncing ? <CircularProgress size={14} /> : <Sync />}
                sx={{
                  fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                  borderColor: panelBorder, color: 'text.secondary',
                  '&:hover': { borderColor: N.sand, color: N.sand }
                }}
              >
                {syncing ? 'Syncing…' : 'Sync'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Alerts ── */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}
      {saveMsg && (
        <Alert
          severity={saveMsg.includes('ailed') || saveMsg.includes('locked') ? 'error' : 'success'}
          onClose={() => setSaveMsg(null)}
          sx={{ mb: 2, fontSize: '0.8rem' }}
        >
          {saveMsg}
        </Alert>
      )}

      {/* ── Tabs ── */}
      <Tabs
        value={tab}
        onChange={(_, v: 'bracket' | 'predictions' | 'leaderboard') => setTab(v)}
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': { bgcolor: N.sand, height: 2 },
          '& .MuiTab-root': {
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.07em',
            textTransform: 'uppercase', color: 'text.secondary',
            minHeight: 40, px: 2,
            '&.Mui-selected': { color: N.sand },
          },
        }}
      >
        <Tab
          icon={<SportsSoccer sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label="Bracket"
          value="bracket"
          id="tab-bracket"
        />
        <Tab
          icon={<FormatListBulleted sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label="Predictions"
          value="predictions"
          id="tab-predictions"
        />
        <Tab
          icon={
            <Badge badgeContent={leaderboard.length || null} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>
              <Leaderboard sx={{ fontSize: 16 }} />
            </Badge>
          }
          iconPosition="start"
          label="Leaderboard"
          value="leaderboard"
          id="tab-leaderboard"
        />
      </Tabs>

      {/* ── Content ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ color: N.sand, mb: 2 }} />
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              Loading match data…
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          {tab === 'bracket' && (
            <BracketView
              matches={matches}
              predictions={predictions}
              isDark={isDark}
              onPredictionChange={handlePredictionChange}
              pendingPreds={pendingPreds}
            />
          )}
          {tab === 'predictions' && (
            <UserPredictionsView
              currentUserId={user?.id ?? -1}
              isDark={isDark}
            />
          )}
          {tab === 'leaderboard' && (
            <LeaderboardView
              entries={leaderboard}
              currentUserId={user?.id ?? -1}
              isDark={isDark}
            />
          )}
        </>
      )}

      {/* ── Scoring legend ── */}
      {tab === 'bracket' && !loading && (
        <Box sx={{
          mt: 4, pt: 2, borderTop: `1px dashed ${panelBorder}`,
          display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start'
        }}>
          <Box>
            <Typography sx={{
              fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'text.disabled', mb: 0.75
            }}>
              Scoring Rules
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                { label: 'Exact Score', pts: '+10 pts' },
                { label: 'Correct Winner', pts: '+5–9 pts' },
                { label: 'Wrong', pts: '0 pts' },
                { label: 'Most Exact Scores', pts: '+10 pts bonus' },
              ].map(r => (
                <Box key={r.label} sx={{
                  px: 1, py: 0.4,
                  border: `1px solid ${panelBorder}`,
                  borderRadius: 0.5,
                  display: 'flex', gap: 0.75, alignItems: 'center'
                }}>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{r.label}</Typography>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, fontFamily: 'monospace', color: N.sand }}>
                    {r.pts}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box>
            <Typography sx={{
              fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'text.disabled', mb: 0.75
            }}>
              Tie-Breakers (single winner)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
              {[
                '1. Most points',
                '2. Most exact scores',
                '3. Most correct winners',
                '4. Lowest total score error',
              ].map(rule => (
                <Typography key={rule} sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                  {rule}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WC2026Page;
